import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, Shield, Check, Eye, Pencil, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  admin: 'Admin',
  sales_manager: 'Sales Manager',
  account_executive: 'Account Executive',
  finance: 'Finance',
  top_management: 'Top Management',
};

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  sales_manager: 'bg-purple-100 text-purple-800',
  account_executive: 'bg-blue-100 text-blue-800',
  finance: 'bg-green-100 text-green-800',
  top_management: 'bg-amber-100 text-amber-800',
};

const MODULE_GROUPS = [
  {
    label: 'Analytics & Dashboard',
    modules: ['intelligence', 'dashboard', 'funnel_analytics'],
  },
  {
    label: 'Sales & Leads',
    modules: ['leads', 'import_leads', 'field_data', 'tags', 'products'],
  },
  {
    label: 'Activities',
    modules: ['events', 'appointments', 'tasks', 'communications', 'calendar'],
  },
  {
    label: 'Finance & Contracts',
    modules: ['customers', 'contracts', 'booking_forms', 'installments', 'payments'],
  },
  {
    label: 'Admin & Settings',
    modules: ['automation', 'landing_pages', 'user_requests', 'accounting', 'users'],
  },
];

export default function RoleSettingsPanel({ currentUserRole }) {
  const [moduleDefinitions, setModuleDefinitions] = useState([]);
  const [configurableRoles, setConfigurableRoles] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [modsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/role-settings/modules`, { credentials: 'include' }),
        fetch(`${API_URL}/api/role-settings`, { credentials: 'include' }),
      ]);
      if (modsRes.ok) {
        const modsData = await modsRes.json();
        setModuleDefinitions(modsData.modules || []);
        const roles = modsData.configurable_roles || [];
        setConfigurableRoles(roles);
        if (!selectedRole && roles.length > 0) setSelectedRole(roles[0]);
      }
      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
    } catch (error) {
      logger.error('Error fetching role settings:', error);
      toast.error('Failed to load role settings');
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getModuleSetting = (role, moduleId) => {
    return settings[role]?.[moduleId] || { enabled: false, view: false, edit: false, delete: false };
  };

  const toggleModule = (moduleId, field) => {
    if (!selectedRole) return;
    setSettings(prev => {
      const roleSettings = { ...(prev[selectedRole] || {}) };
      const modSettings = { ...(roleSettings[moduleId] || { enabled: false, view: false, edit: false, delete: false }) };

      if (field === 'enabled') {
        const newEnabled = !modSettings.enabled;
        modSettings.enabled = newEnabled;
        if (!newEnabled) {
          modSettings.view = false;
          modSettings.edit = false;
          modSettings.delete = false;
        } else {
          modSettings.view = true;
        }
      } else {
        modSettings[field] = !modSettings[field];
        if (field === 'view' && !modSettings.view) {
          modSettings.edit = false;
          modSettings.delete = false;
        }
        if ((field === 'edit' || field === 'delete') && modSettings[field]) {
          modSettings.view = true;
        }
      }

      roleSettings[moduleId] = modSettings;
      return { ...prev, [selectedRole]: roleSettings };
    });
  };

  const saveRoleSettings = async () => {
    if (!selectedRole) return;
    setSaving(selectedRole);
    try {
      const response = await fetch(`${API_URL}/api/role-settings/${selectedRole}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modules: settings[selectedRole] || {} }),
      });
      if (response.ok) {
        toast.success(`${ROLE_LABELS[selectedRole]} settings saved`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(null);
    }
  };

  const getModuleDef = (moduleId) => moduleDefinitions.find(m => m.id === moduleId);

  // Check if current user (admin) can modify the selected role
  const canModify = currentUserRole === 'owner' || (currentUserRole === 'admin' && selectedRole !== 'admin');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading role settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="role-settings-panel">
      {/* Role Selector Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="role-tabs">
        {configurableRoles.map(r => (
          <button
            key={r}
            onClick={() => setSelectedRole(r)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedRole === r
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`role-tab-${r}`}
          >
            {ROLE_LABELS[r] || r}
          </button>
        ))}
      </div>

      {selectedRole && (
        <>
          {/* Role Header */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${ROLE_COLORS[selectedRole] || 'bg-slate-100'}`}>
                    <Shield size={20} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{ROLE_LABELS[selectedRole]} Permissions</CardTitle>
                    <CardDescription>
                      Configure which modules and actions are available for this role
                    </CardDescription>
                  </div>
                </div>
                <Button
                  onClick={saveRoleSettings}
                  disabled={!canModify || saving === selectedRole}
                  data-testid="save-role-settings"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {saving === selectedRole ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Save Changes</>
                  )}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {!canModify && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
              <Info size={16} />
              <span>Admin role settings can only be modified by the platform Owner.</span>
            </div>
          )}

          {/* Module Groups */}
          {MODULE_GROUPS.map(group => (
            <Card key={group.label}>
              <CardHeader className="py-3 bg-slate-50/70 border-b">
                <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                  {group.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {group.modules.map(moduleId => {
                    const modDef = getModuleDef(moduleId);
                    if (!modDef) return null;
                    const modSettings = getModuleSetting(selectedRole, moduleId);
                    const hasEdit = modDef.actions.includes('edit');
                    const hasDelete = modDef.actions.includes('delete');

                    return (
                      <div
                        key={moduleId}
                        className={`flex items-center justify-between px-4 py-3 ${!modSettings.enabled ? 'opacity-50' : ''}`}
                        data-testid={`module-row-${moduleId}`}
                      >
                        <div className="flex items-center gap-3 min-w-[180px]">
                          <Switch
                            checked={modSettings.enabled}
                            onCheckedChange={() => toggleModule(moduleId, 'enabled')}
                            disabled={!canModify}
                            data-testid={`toggle-${moduleId}-enabled`}
                          />
                          <span className={`text-sm font-medium ${modSettings.enabled ? 'text-slate-900' : 'text-slate-400'}`}>
                            {modDef.label}
                          </span>
                        </div>

                        {modSettings.enabled && (
                          <div className="flex items-center gap-4">
                            {/* View */}
                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={modSettings.view}
                                onChange={() => toggleModule(moduleId, 'view')}
                                disabled={!canModify}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                data-testid={`check-${moduleId}-view`}
                              />
                              <Eye size={14} className="text-slate-500" />
                              <span className="text-xs text-slate-600">View</span>
                            </label>

                            {/* Edit */}
                            {hasEdit && (
                              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={modSettings.edit}
                                  onChange={() => toggleModule(moduleId, 'edit')}
                                  disabled={!canModify}
                                  className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                  data-testid={`check-${moduleId}-edit`}
                                />
                                <Pencil size={14} className="text-slate-500" />
                                <span className="text-xs text-slate-600">Edit</span>
                              </label>
                            )}

                            {/* Delete */}
                            {hasDelete && (
                              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={modSettings.delete}
                                  onChange={() => toggleModule(moduleId, 'delete')}
                                  disabled={!canModify}
                                  className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                                  data-testid={`check-${moduleId}-delete`}
                                />
                                <Trash2 size={14} className="text-slate-500" />
                                <span className="text-xs text-slate-600">Delete</span>
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
